import {
    Combobox,
    ComboboxOption,
    ComboboxInput,
    ComboboxOptions,
} from '@headlessui/react';
import CallAdaptiveButton from 'flow/components/CallAdaptiveButton';
import Button from 'flow/components/ui-components/Button';
import Icon from 'flow/components/ui-components/Icon';
import NumberWrapper from 'flow/components/ui-components/NumberWrapper';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { fuzzySearchColleaguesList } from 'flow/selectors/search-selectors';
import { FuzzySearchItem } from 'flow/types/Contact';

import { stripAndConvertNumberToE164 } from 'flow/utils/call-utils';
import { twMerge } from 'tailwind-merge';
import React, {
    ClipboardEvent,
    useCallback,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import { useAppSelector } from 'flow/hooks/useRedux';
import { shallowEqual } from 'react-redux';
import ContactAvatar from './ui-components/ContactAvatar';
import { isValidPartialPhoneNumber } from 'flow/utils/phone-utils';
import {
    selectOwnExtensionKey,
    selectUserCountryCode,
} from 'flow/selectors/ExtensionSelectors';
import Keypad from './dialpad/Keypad';

interface Props {
    className?: string;
    disableCallButton?: boolean;
    setContact?: (contact: string) => void;
    isMenu?: boolean;
    onEnter?: (number: string) => void;
}

const DIAL_CONTACT_ITEM_HEIGHT = 60;
const DIAL_CONTACT_MAX__DISPLAY_COUNT = 3;

const Dialpad = ({
    className,
    setContact,
    disableCallButton,
    isMenu,
    onEnter,
}: Props) => {
    const { i18next, embedded, setShowDialpad } = useAppContext();
    const { openModal } = useModal();
    const [query, setQuery] = useState('');
    const [selectedExtension, setSelectedExtension] = useState(undefined);
    const [isSearching, setIsSearching] = useState(true);

    const searchVariableListRef = useRef<VariableSizeList>();

    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );
    const countryCode = useAppSelector(selectUserCountryCode);
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const callableNumber = stripAndConvertNumberToE164(query, countryCode);

    const validKeys = new Set(['fixedNumber', 'mobileNumber']);

    const sortedSearchResults = useAppSelector(
        (state) =>
            fuzzySearchColleaguesList(state, query).filter(
                ({ item, matches }) =>
                    item.key !== ownExtensionKey &&
                    matches.some((match: { key: string }) =>
                        validKeys.has(match['key']),
                    ),
            ),
        shallowEqual,
    );

    const onChange = (contact: {
        item: { fixedNumber: string; mobileNumber: string; key: string };
    }) => {
        handleChange(
            contact?.item.fixedNumber || contact?.item.mobileNumber,
            false,
        );
        setSelectedExtension(contact?.item.key);
    };

    const handleChange = (query: string, isSearching = true) => {
        setQuery(query);
        setIsSearching(isSearching);
        if (setContact) {
            setContact(query);
        }
        if (!query) {
            setSelectedExtension(undefined);
        }
    };

    const comboList = useCallback(
        ({ index, data, style }: ListChildComponentProps) => {
            const contact = data.get(index);
            let matchedNumber = '';

            switch (contact.matches?.[0]?.key) {
                case 'mobileNumber':
                    matchedNumber = contact?.item.mobileNumber;
                    break;
                case 'fixedNumber':
                    matchedNumber = contact?.item.fixedNumber;
                    break;
                case 'technicalNumber':
                    matchedNumber = contact?.item.technicalNumber;
                    break;

                default:
                    matchedNumber =
                        contact?.item.fixedNumber || contact?.item.mobileNumber;
                    break;
            }

            return (
                <div style={style}>
                    <ComboboxOption
                        key={contact?.item.key}
                        value={contact}
                        className={({ focus }) =>
                            twMerge(
                                'relative cursor-default px-4 py-2 select-none',
                                focus ? 'bg-gray-200' : 'text-gray-900',
                            )
                        }
                    >
                        {({ focus, selected }) => (
                            <div className="flex items-center overflow-x-hidden">
                                <ContactAvatar
                                    contact={extensions.getIn([
                                        contact.item.key,
                                        'contact',
                                    ])}
                                    lazyLoad={true}
                                />
                                <div className="ps-2">
                                    <div
                                        className={twMerge(
                                            'truncate text-gray-500',
                                            selected && 'font-medium',
                                        )}
                                    >
                                        {contact?.item.name}
                                    </div>

                                    <div
                                        className={twMerge(
                                            'text-sm',
                                            focus
                                                ? 'text-gray-800'
                                                : 'text-gray-500',
                                        )}
                                    >
                                        <NumberWrapper>
                                            {matchedNumber}
                                        </NumberWrapper>
                                    </div>
                                </div>
                            </div>
                        )}
                    </ComboboxOption>
                </div>
            );
        },
        [extensions],
    );

    if (searchVariableListRef.current) {
        searchVariableListRef.current.resetAfterIndex(0, false);
    }

    const searchVariableListHeight = useMemo(
        () =>
            Math.min(
                sortedSearchResults.size * DIAL_CONTACT_ITEM_HEIGHT,
                DIAL_CONTACT_ITEM_HEIGHT * DIAL_CONTACT_MAX__DISPLAY_COUNT,
            ),
        [sortedSearchResults],
    );

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault();

        const phone = event.clipboardData
            .getData('text')
            .replace(/[^+*#\d]/g, '');

        if (isValidPartialPhoneNumber(phone)) {
            handleChange(phone);
        }
    };

    const handleCreateContact = () => {
        openModal('createContact', {
            initialNumber: query,
            countryCode,
        });
    };

    const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!callableNumber || !onEnter) return;
        if (event.key === 'Enter') {
            onEnter(callableNumber);
        }
    };

    return (
        <div
            className={twMerge(
                'flex flex-col bg-white pb-3 dark:bg-gray-100',
                isMenu ? '' : 'rounded-xl',
                className,
            )}
            id={isMenu ? 'dialpad-sidebar' : 'dialpad'}
            data-test-id={isMenu ? 'dialpad-sidebar' : 'dialpad'}
        >
            {isMenu ? (
                <div className="flex justify-end p-3">
                    <Button
                        iconKey="close"
                        onClick={() => setShowDialpad(false)}
                        aria-label={i18next.t('dialpad.close')}
                    />
                </div>
            ) : null}

            <div
                className={twMerge(
                    'mb-24 flex flex-col items-center gap-3',
                    isMenu ? (embedded ? 'pt-3' : 'pt-3 sm:pt-24') : 'pt-6',
                    isMenu ? 'rounded-xl' : '',
                )}
            >
                <Combobox
                    as="div"
                    value={selectedExtension || query}
                    onChange={onChange}
                >
                    <div className="relative w-full">
                        <ComboboxInput
                            aria-label={i18next.t('input.searchAndCall')}
                            type="tel"
                            id="dialpad-input"
                            autoFocus={!!isMenu}
                            autoComplete="off"
                            className="focus-ring w-full rounded-sm border-1 bg-gray-200 py-3 ps-3 pe-12 text-center text-gray-900"
                            onKeyUp={handleKeyUp}
                            onChange={(event) =>
                                (isValidPartialPhoneNumber(
                                    event.target.value,
                                ) ||
                                    event.target.value.length === 0) &&
                                handleChange(event.target.value)
                            }
                            value={query}
                            displayValue={(contact: FuzzySearchItem) =>
                                query || contact?.item?.fixedNumber
                            }
                            onPaste={handlePaste}
                        />

                        {/* A plain button, not ComboboxButton. This used to
                            be an icon button inside ComboboxButton -- a button
                            within a button -- with aria-hidden on the inner one
                            to stop it being announced twice, which left it
                            unreachable by assistive tech. ComboboxButton cannot
                            take its place: HeadlessUI forces tabIndex -1 on it,
                            because in a combobox you tab to the input, and the
                            prop does not override it. The classes are the ones
                            ComboboxButton carried, so the box is unchanged. */}
                        <button
                            type="button"
                            aria-label={i18next.t('action.delete_last_digit')}
                            className="absolute inset-y-0 end-0 flex items-center rounded-e-md px-2"
                            // Keep the caret in the field rather than move
                            // focus back to it after the click. Enter and Space
                            // fire click before their own keyup, so a focus
                            // move lands that keyup on the input -- which reads
                            // Enter as "call this number". Suppressing the
                            // mousedown default means a pointer never takes
                            // focus away in the first place.
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() =>
                                handleChange(
                                    query.substring(0, query.length - 1),
                                )
                            }
                        >
                            <span className="hover:darken-10 flex items-center justify-center rounded-sm p-1">
                                <Icon
                                    iconKey="backspace"
                                    className="size-5 text-gray-400"
                                />
                            </span>
                        </button>

                        {sortedSearchResults.size && isSearching ? (
                            <ComboboxOptions
                                className="absolute w-full"
                                data-test-id="dialpad-options"
                            >
                                <VariableSizeList
                                    className="absolut scrollbar shadow-base dark:bg-gray-150 z-10 mt-1 max-h-32 overflow-auto rounded-md bg-white text-base focus:outline-hidden"
                                    ref={searchVariableListRef}
                                    height={searchVariableListHeight}
                                    itemCount={sortedSearchResults.size}
                                    itemSize={() => DIAL_CONTACT_ITEM_HEIGHT}
                                    width="100%"
                                    overscanCount={6}
                                    itemData={sortedSearchResults}
                                >
                                    {comboList}
                                </VariableSizeList>
                            </ComboboxOptions>
                        ) : null}
                    </div>
                </Combobox>

                {!selectedExtension &&
                sortedSearchResults.size === 0 &&
                query !== '' ? (
                    <Button
                        className={twMerge(
                            'border-white py-2 text-sm/4',
                            selectedExtension ? 'invisible' : '',
                        )}
                        onClick={handleCreateContact}
                    >
                        {i18next.t('dialpad.add.contact')}
                    </Button>
                ) : (
                    <div className="mb-8" />
                )}
            </div>

            <div className="flex flex-col gap-3">
                <Keypad onClick={handleChange} value={query} />

                {disableCallButton ? null : (
                    <div
                        className="mt-3 flex h-12 justify-center"
                        data-test-id="dialpad-call-button"
                    >
                        <CallAdaptiveButton
                            phoneNumber={callableNumber}
                            extensionToCall={extensions.get(selectedExtension)}
                            size="large"
                            activeBg={true}
                            rounded={true}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dialpad;
